import { itx, noItx } from "../../../src/translationItx/itx";
import { loadPdfDocumentHeader, type PdfDocumentHeader } from "../../../src/pdf/PdfDocumentHeader";
import { protectPdfOutput } from "../../../src/pdf/protectPdfOutput";
import { jsPDF } from "jspdf";
import "svg2pdf.js";
import { PDFDocument } from "pdf-lib";
import { C1_PdfImageOptimizer } from "../lib/C1_PdfImages";
import { registerC1PdfFonts } from "../lib/C1_PdfFonts";
import { deduplicateC1PdfImages } from "../lib/C1_PdfImageDeduplication";
import { C1_DOCUMENT_LOGO, C1_PRODUCT_NAME, ESTADA_PDF_COVER_LOGO } from "../C1_Const";
import {
  getC1DocumentationMarkdown,
  createC1MarkdownCoverPdfBlob,
} from "../raumbuch/C1_RaumbuchCoverMarkdown";
import type { C1_SerializedPlanSvg } from "./C1_PlanSvg";
import { addC1PdfOutlines, addC1PdfPageLinks } from "../lib/C1_PdfOutlines";
import { createC1PdfTableOfContentsBlob } from "../lib/C1_PdfTableOfContents";
import type { C1_PdfChapterArtifact } from "../lib/C1_PdfChapter";
import { c1PdfFileName } from "../lib/C1_PdfFileName";

export type C1_FloorPlanPdfPage = C1_SerializedPlanSvg & {
  id: string;
  label: string;
  roomRects: Array<{ roomId: string; x: number; y: number; width: number; height: number }>;
};

type C1_FloorPlanPdfOptions = {
  projectName: string;
  dateText: string;
  floors: C1_FloorPlanPdfPage[];
};

const PDF_PRODUCT_NAME = `ESTADA ${C1_PRODUCT_NAME}`;
const lineColor = "#aeb9bf";
const mutedColor = "#586a75";

function omitEquipmentHoverPreviews(svg: SVGSVGElement): void {
  svg
    .querySelectorAll(
      ".C1_svgButtonHoverPreview, .C1_svgLightHoverPreview, .C1_svgSensorHoverPreview, .C1_svgLightHitArea",
    )
    .forEach((preview) => {
    preview.remove();
    });
}

function normalizeNestedSvgDimensions(svg: SVGSVGElement): void {
  svg.querySelectorAll<SVGSVGElement>("svg").forEach((nestedSvg) => {
    for (const property of ["width", "height"] as const) {
      const styleValue = nestedSvg.style.getPropertyValue(property).trim();
      if (styleValue && !Number.isFinite(Number.parseFloat(styleValue))) {
        nestedSvg.style.removeProperty(property);
      }
    }
    nestedSvg.setAttribute("overflow", "visible");
  });
}



/** Supplies the floor identity to the shared document header. */
function drawHeader(document: jsPDF, floor: C1_FloorPlanPdfPage, header: PdfDocumentHeader) {
  header.draw(document, { planType: itx("c1.ui.Etagen_Grundriss", "Etagen-Grundriss"), hierarchy: ["", floor.label] });
}

function drawFooter(
  document: jsPDF,
  options: C1_FloorPlanPdfOptions,
  pageNumber: number,
  totalPages: number,
  pageWidth: number,
  pageHeight: number,
) {
  const lineY = pageHeight - 48;
  document.setDrawColor(lineColor);
  document.setLineWidth(1);
  document.line(45, lineY, pageWidth - 45, lineY);
  document.setTextColor(mutedColor);
  document.setFont("helvetica", "normal");
  document.setFontSize(6.5);
  document.text(itx("c1.ui.Projekt_value1", "Projekt: {{value1}}", { "{{value1}}": String(options.projectName) }), 45, lineY + 14);
  document.text(itx("c1.ui.Stand_value1", "Stand: {{value1}}", { "{{value1}}": String(options.dateText) }), pageWidth / 2, lineY + 14, { align: "center" });
  document.text(`${pageNumber} / ${totalPages}`, pageWidth - 45, lineY + 14, { align: "right" });
}

function probeSvgForElement(svg: SVGSVGElement, element: Element): SVGSVGElement {
  if (element === svg) return svg.cloneNode(true) as SVGSVGElement;

  const probeSvg = svg.cloneNode(false) as SVGSVGElement;
  const ancestors: Element[] = [];
  let ancestor: Element | null = element.parentElement;
  while (ancestor && ancestor !== svg) {
    ancestors.push(ancestor);
    ancestor = ancestor.parentElement;
  }

  let parent: Element = probeSvg;
  for (const source of ancestors.reverse()) {
    const clone = source.cloneNode(false) as Element;
    parent.appendChild(clone);
    parent = clone;
  }
  parent.appendChild(element.cloneNode(true));
  return probeSvg;
}

async function probeSvgElement(svg: SVGSVGElement, element: Element): Promise<unknown | null> {
  const probeDocument = new jsPDF({ orientation: "landscape", unit: "pt", format: "a3" });
  try {
    await probeDocument.svg(probeSvgForElement(svg, element), { x: 0, y: 0, width: 500, height: 500 });
    return null;
  } catch (error) {
    return error;
  }
}

function describeSvgElement(element: Element): Record<string, string | null> {
  return {
    tag: element.tagName,
    id: element.getAttribute("id"),
    class: element.getAttribute("class"),
    equipmentKind: element.getAttribute("data-c1-equipment-kind"),
    equipmentId: element.getAttribute("data-c1-equipment-id"),
    layer: element.getAttribute("data-c1-svg-layer"),
    width: element.getAttribute("width"),
    height: element.getAttribute("height"),
    x: element.getAttribute("x"),
    y: element.getAttribute("y"),
    viewBox: element.getAttribute("viewBox"),
    transform: element.getAttribute("transform"),
    href: element.getAttribute("href") ?? element.getAttribute("xlink:href"),
  };
}

async function reportFailingDescendants(
  svg: SVGSVGElement,
  parent: Element,
  path: string,
  depth = 0,
): Promise<void> {
  if (depth >= 12) {
    console.warn(noItx("Maximum diagnostic depth reached."), path, describeSvgElement(parent));
    return;
  }

  const children = Array.from(parent.children);
  if (children.length === 0) {
    console.warn(noItx("Invalid SVG leaf node."), path, describeSvgElement(parent));
    return;
  }

  let foundFailingChild = false;
  for (let index = 0; index < children.length; index += 1) {
    const child = children[index];
    const childPath = `${path} > ${child.tagName.toLowerCase()}[${index}]`;
    const error = await probeSvgElement(svg, child);
    if (!error) continue;
    foundFailingChild = true;
    console.warn(noItx("Invalid SVG child node."), childPath, describeSvgElement(child), error);
    await reportFailingDescendants(svg, child, childPath, depth + 1);
  }

  if (!foundFailingChild) {
    console.warn(
      noItx("The error occurs only when the child nodes are combined."),
      path,
      describeSvgElement(parent),
      children.map(describeSvgElement),
    );
  }
}

async function omitAndReportUnrenderableElements(svg: SVGSVGElement, floorLabel: string): Promise<void> {
  const completeError = await probeSvgElement(svg, svg);
  if (!completeError) return;

  console.group(noItx(`PDF preflight failed: ${floorLabel}`));
  console.error(noItx("The complete floor plan SVG cannot be rendered."), describeSvgElement(svg), completeError);

  const equipmentItems = Array.from(
    svg.querySelectorAll<SVGGElement>("[data-c1-equipment-kind]"),
  );
  for (const item of equipmentItems) {
    if (!svg.contains(item)) continue;
    const error = await probeSvgElement(svg, item);
    if (!error) continue;
    console.warn(noItx("Removing invalid equipment from the PDF copy."), describeSvgElement(item), error);
    await reportFailingDescendants(
      svg,
      item,
      `equipment:${item.getAttribute("data-c1-equipment-id") ?? "ohne-id"}`,
    );
    item.remove();
  }

  const nestedSvgs = Array.from(svg.querySelectorAll<SVGSVGElement>("svg"));
  for (const nestedSvg of nestedSvgs) {
    if (!svg.contains(nestedSvg)) continue;
    const error = await probeSvgElement(svg, nestedSvg);
    if (!error) continue;
    console.warn(noItx("Removing invalid nested SVG from the PDF copy."), describeSvgElement(nestedSvg), error);
    nestedSvg.remove();
  }

  const remainingError = await probeSvgElement(svg, svg);
  if (remainingError) {
    const layers = Array.from(svg.querySelectorAll<SVGGElement>("[data-c1-svg-layer]"));
    for (const layer of layers) {
      const error = await probeSvgElement(svg, layer);
      if (error) console.error(noItx("Invalid plan layer found."), describeSvgElement(layer), error);
    }
    console.error(noItx("SVG is still invalid after individual node checks."), remainingError);
  } else {
    console.info(noItx("SVG can be rendered after excluding the logged nodes."));
  }
  console.groupEnd();
}

export async function downloadC1FloorPlanPdf(
  options: C1_FloorPlanPdfOptions,
  download = true,
  chapterOnly = false,
): Promise<C1_PdfChapterArtifact> {
  const document = new jsPDF({ orientation: "landscape", unit: "pt", format: "a3", compress: true });
  await registerC1PdfFonts(document);
  const imageOptimizer = new C1_PdfImageOptimizer();
  document.setProperties({ title: `${PDF_PRODUCT_NAME} ${itx("c1.ui.Grundrisse", "Grundrisse")}`, author: "ESTADA" });
  const header = await loadPdfDocumentHeader();
  const tableOfContents = await createC1PdfTableOfContentsBlob({
    projectName: options.projectName,
    entries: options.floors.map((floor, index) => ({ title: floor.label, contentPageIndex: index })),
    pageSize: "A3",
    orientation: "landscape",
  });
  const chapterPages: C1_PdfChapterArtifact["pages"] = [];

  for (let index = 0; index < options.floors.length; index += 1) {
    if (index > 0) document.addPage("a3", "landscape");
    const floor = options.floors[index];
    const pageWidth = document.internal.pageSize.getWidth();
    const pageHeight = document.internal.pageSize.getHeight();
    drawHeader(document, floor, header);
    drawFooter(
      document,
      options,
      index + 3 + tableOfContents.pageCount,
      options.floors.length + 2 + tableOfContents.pageCount,
      pageWidth,
      pageHeight,
    );

    const content = { x: 45, y: 82, width: pageWidth - 90, height: pageHeight - 140 };
    const scale = Math.min(content.width / floor.width, content.height / floor.height);
    const width = floor.width * scale;
    const height = floor.height * scale;
    const planX = content.x + (content.width - width) / 2;
    const planY = content.y + (content.height - height) / 2;
    chapterPages.push({
      title: floor.label,
      pageIndex: index,
      sourceIds: [floor.id],
      links: floor.roomRects.map((room) => ({
        targetId: room.roomId,
        x: planX + room.x * scale,
        y: planY + room.y * scale,
        width: room.width * scale,
        height: room.height * scale,
      })),
    });
    const svg = new DOMParser().parseFromString(floor.markup, "image/svg+xml")
      .documentElement as unknown as SVGSVGElement;
    // svg2pdf otherwise creates a viewport clip via jsPDF.rect(). That call is
    // brittle when browser-derived SVG dimensions are not plain finite numbers.
    // The PDF page already clips its content, so this additional SVG clip is unnecessary.
    svg.setAttribute("overflow", "visible");
    omitEquipmentHoverPreviews(svg);
    normalizeNestedSvgDimensions(svg);
    await imageOptimizer.optimize(svg, width, height);
    await omitAndReportUnrenderableElements(svg, floor.label);
    await document.svg(svg, {
      x: planX,
      y: planY,
      width,
      height,
    });
  }

  const contentBlob = await protectPdfOutput(document.output("blob"), "plan");
  const coverBlob = await createC1MarkdownCoverPdfBlob({
    documentName: itx("c1.ui.Grundrisse", "Grundrisse"),
    projectName: options.projectName,
    dateText: options.dateText,
    logo: ESTADA_PDF_COVER_LOGO,
    pageSize: "A3",
    orientation: "landscape",
  });
  const documentationBlob = await createC1MarkdownCoverPdfBlob({
    documentName: itx("c1.ui.Grundrisse", "Grundrisse"),
    markdownTemplate: getC1DocumentationMarkdown(),
    textAlign: "left",
    projectName: options.projectName,
    dateText: options.dateText,
    logo: C1_DOCUMENT_LOGO,
    pageSize: "A3",
    orientation: "landscape",
  });
  const mergedDocument = await PDFDocument.create();
  const coverDocument = await PDFDocument.load(await coverBlob.arrayBuffer());
  const documentationDocument = await PDFDocument.load(await documentationBlob.arrayBuffer());
  const tableOfContentsDocument = await PDFDocument.load(await tableOfContents.blob.arrayBuffer());
  const floorPlanDocument = await PDFDocument.load(await contentBlob.arrayBuffer());
  const [coverPage] = await mergedDocument.copyPages(coverDocument, [0]);
  mergedDocument.addPage(coverPage);
  const [documentationPage] = await mergedDocument.copyPages(documentationDocument, [0]);
  mergedDocument.addPage(documentationPage);
  const tableOfContentsPages = await mergedDocument.copyPages(
    tableOfContentsDocument,
    tableOfContentsDocument.getPageIndices(),
  );
  tableOfContentsPages.forEach((page) => mergedDocument.addPage(page));
  const floorPlanPages = await mergedDocument.copyPages(
    floorPlanDocument,
    floorPlanDocument.getPageIndices(),
  );
  floorPlanPages.forEach((page) => mergedDocument.addPage(page));

  addC1PdfOutlines(mergedDocument, [
    { title: itx("c1.ui.Deckblatt", "Deckblatt"), pageIndex: 0 },
    { title: itx("c1.ui.Dokumentation", "Dokumentation"), pageIndex: 1 },
    { title: itx("pageDocumentType.coverPage", "Inhaltsverzeichnis"), pageIndex: 2 },
    ...options.floors.map((floor, index) => ({
      title: floor.label,
      pageIndex: index + 2 + tableOfContents.pageCount,
    })),
  ]);
  addC1PdfPageLinks(
    mergedDocument,
    tableOfContents.links.map((link) => ({
      ...link,
      sourcePageIndex: 2 + link.tableOfContentsPageIndex,
      targetPageIndex: 2 + tableOfContents.pageCount + link.contentPageIndex,
    })),
  );

  await deduplicateC1PdfImages(mergedDocument);
  const mergedBytes = await mergedDocument.save();
  const mergedBuffer = new ArrayBuffer(mergedBytes.byteLength);
  new Uint8Array(mergedBuffer).set(mergedBytes);
  const blob = new Blob([mergedBuffer], { type: "application/pdf" });
  if (download) {
    const url = URL.createObjectURL(blob);
    const link = window.document.createElement("a");
    link.href = url;
    link.download = c1PdfFileName(options.projectName);
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
  }

  if (chapterOnly) return { blob: contentBlob, pages: chapterPages };
  return { blob, pages: chapterPages };
}
